import events, { eventEmitter } from '@/events';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';
import { getCookie, parseConsentCookie } from './utils';

// Debug mode is enabled by default for local development
// and can be used during initialization (see component setup)
let clarityDebug = false;

const isLocalhost = window.location.hostname === 'localhost';
const isTestEnv = window.location.href.startsWith('https://test');
const PROJECT_ID = isLocalhost ? '' : process.env.CLARITY_KEY;

const debugLog = (...args) => {
  if (clarityDebug && isLocalhost) {
    // eslint-disable-next-line no-console
    console.log('[Clarity]', ...args);
  }
};

export const emitClarityTrackEvent = event => {
  debugLog('Dispatching event:', event);
  eventEmitter.emit(events.clarity.TRACK, event);
};

export * from './constants';

const useClarity = (debug = false, debugFunction = debugLog) => {
  const clarityRef = useRef(null);
  const clarityListenerRegistered = useRef(false);

  const handleTrackEvent = useCallback(({ namespace, event }) => {
    if (!clarityRef.current) {
      debugFunction('Clarity is not available yet.');
      return;
    }

    const eventName = `${namespace}:${event}`;

    if (isFunction(clarityRef.current.setEvent)) {
      clarityRef.current.setEvent(eventName);
      debugFunction('Track event:', eventName);
    } else {
      debugFunction('Clarity or setEvent is not available');
    }
  }, []);

  const initClarity = useCallback(
    async isConsentGiven => {
      try {
        const { clarity } = await import('react-microsoft-clarity');

        if (clarity && !clarity.hasStarted()) {
          clarity.init(PROJECT_ID);
          clarity.consent(isConsentGiven);
          debugFunction('Clarity initialized and consent applied');
        }

        clarityRef.current = clarity;

        if (!clarityListenerRegistered.current) {
          eventEmitter.on(events.clarity.TRACK, handleTrackEvent);
          clarityListenerRegistered.current = true;
        }
      } catch (err) {
        console.error('[Clarity] Failed to load Clarity:', err);
      }
    },
    [handleTrackEvent],
  );

  const handleConsentUpdate = useCallback(
    eventData => {
      const data = eventData?.detail;
      const accepted = Array.isArray(data?.accepted) ? data.accepted : [];
      const isConsentGiven = accepted.includes('analytics');
      debugFunction('Consent granted:', isConsentGiven);

      if (isConsentGiven && Boolean(PROJECT_ID) && !(isLocalhost || isTestEnv)) {
        initClarity(isConsentGiven);
      } else {
        debugFunction('Clarity not initialized due to missing consent or PROJECT_ID');
        // Consent withdrawn or unsuitable config: disable Clarity tracking
        if (clarityRef.current) {
          if (isFunction(clarityRef.current.consent)) {
            clarityRef.current.consent(false);
          }
          if (isFunction(clarityRef.current.stop)) {
            clarityRef.current.stop();
          }
          clarityRef.current = null;
          debugFunction('Clarity tracking disabled due to consent withdrawal or configuration');
        }
        eventEmitter.off(events.clarity.TRACK, handleTrackEvent);
        clarityListenerRegistered.current = false;
      }
    },
    [initClarity, clarityRef, handleTrackEvent],
  );

  useEffect(() => {
    clarityDebug = debug;
  }, [debug]);

  useEffect(() => {
    if (isTestEnv || isLocalhost) return; // do not track ms clarity for test env
    document.addEventListener('cookieyes_consent_update', handleConsentUpdate);

    const consentCookie = getCookie('cookieyes-consent');
    const consentObj = parseConsentCookie(consentCookie);

    if (consentObj.analytics === 'yes' && Boolean(PROJECT_ID)) {
      initClarity(true);
    }

    return () => {
      eventEmitter.off(events.clarity.TRACK, handleTrackEvent);
      clarityListenerRegistered.current = false;
      // Ensure clarity is stopped correctly on cleanup
      if (clarityRef.current && isFunction(clarityRef.current.stop)) {
        clarityRef.current.stop();
        clarityRef.current = null;
        debugFunction('Clarity tracking stopped on component unmount');
      }
      document.removeEventListener('cookieyes_consent_update', handleConsentUpdate);
    };
  }, [handleTrackEvent, handleConsentUpdate]);

  return null;
};

export const Clarity = ({ debug = false }) => {
  useClarity(debug);

  return null;
};

Clarity.propTypes = {
  debug: PropTypes.bool,
};

export default Clarity;
